import AddSharp from "@mui/icons-material/AddSharp";
import { Grid, Typography, useTheme } from "@mui/material";
import AcxButton from "components/UI/AcxButton";
import { observer } from "mobx-react-lite";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import LocalStorage from "stores/LocalStorage";
import { useStore } from "utils/useStore";
import { AuthStore } from "../../../../../stores/AuthStore";
import useOrganizationRoles from "../../hooks/useOrganizationRoles";
import useOrganizationUsers from "../../hooks/useOrganizationUsers";
import OrganizationRolesDetail from "./OrganizationRolesDetail";
import OrganizationRolesListCards from "./OrganizationRolesListCards";
import OrganizationRolesListSearch from "./OrganizationRolesListSearch";
import OrganizationRolesListTable from "./OrganizationRolesListTable";
import OrganizationRolesListViewSelect from "./OrganizationRolesListViewSelect";

const lsViewTypeKey = "RolesListViewType";

export enum ViewType {
    cardView = "cardView",
    listView = "listView",
}

type Props = {
    orgId: string;
};

const OrganizationRolesList: React.FC<Props> = observer(({ orgId }) => {
    const theme = useTheme();
    // const { search } = useLocation();
    const location = useLocation();
    const { getLocalStore } = useStore(LocalStorage);
    const ls = getLocalStore("AcxDefault");
    const authStore = useStore(AuthStore);
    const { users, isLoading: isLoadingUsers } = useOrganizationUsers(orgId);
    // roles is un-transformed result from API
    const { roles, isLoading, refetch } = useOrganizationRoles(orgId);
    const [filter, setFilter] = useState<string>("");
    const navigate = useNavigate();
    const [filteredRoles, setFilteredRoles] = useState<any[]>([]);
    const [detailData, setDetailData] = useState<any>({
        name: "",
    });
    const [view, setView] = useState<ViewType>();
    // const { id } = qs.parse(search);
    const [isOrgAdmin, setIsOrgAdmin] = useState<boolean>(false);
    const [isExternalUser, setIsExternalUser] = useState<boolean>(false);
    const params = new URLSearchParams(location.search);
    const id = params.get("id");

    useEffect(() => {
        let destroyed = false;
        const getLsData = async () => {
            const viewType: ViewType | null = await ls.getItem(lsViewTypeKey);

            if (!destroyed) {
                setView(viewType || ViewType.cardView);
            }
        };
        getLsData();
        return () => {
            destroyed = true;
        };
    }, [ls]);

    useEffect(() => {
        if (!isLoadingUsers) {
            const role = roles?.find((role) => role.id === id);
            setIsOrgAdmin(role?.isOrgAdministrator ?? false);
            setIsExternalUser(role?.isExternalUser ?? false);
            setDetailData(role || { name: "", permissions: {} });
        }
    }, [id, roles, isLoadingUsers, users]);

    useEffect(() => {
        const filtered = roles?.filter((group) =>
            group.$search?.includes(filter),
        );
        setFilteredRoles(filtered ? [...filtered] : []);
    }, [roles, filter]);

    const changeView = async (view: ViewType) => {
        setView(view);
        await ls.setItem(lsViewTypeKey, view);
    };

    const onCloseDetail = () => {
        navigate(`/admin/organizations/${orgId}/roles`);
    };

    return (
        <>
            {view ? (
                <div style={{width:"85%"}}>
                    <Grid
                        container
                        justifyContent="space-between"
                        alignItems={"flex-end"}
                        sx={{
                            marginTop: theme.spacing(2),
                            marginBottom: theme.spacing(2),
                            alignItems: "center",
                        }}
                    >
                        <Grid item xs={2}>
                            <Typography variant="subtitle1">
                                {!!filteredRoles.length && (
                                    <>{filteredRoles.length} </>
                                )}
                                Roles
                            </Typography>
                        </Grid>
                        <Grid
                            item
                            xs={9}
                            container
                            spacing={1}
                            justifyContent="flex-end"
                            alignItems={"flex-end"}
                            alignContent="flex-end"
                            style={{ margin: 0 }}
                        >
                            <div style={{ display: "flex" }}>
                                <OrganizationRolesListSearch
                                    value={filter}
                                    setFilter={setFilter}
                                />
                                <OrganizationRolesListViewSelect
                                    view={view}
                                    changeView={changeView}
                                />

                                {authStore.canUserEditRoles() && (
                                    <AcxButton
                                        fullWidth
                                        color="primary"
                                        startIcon={<AddSharp />}
                                        onClick={() => {
                                            navigate(
                                                `/admin/organizations/${orgId}/roles?id=add`,
                                            );
                                        }}
                                    >
                                        New role
                                    </AcxButton>
                                )}
                            </div>
                        </Grid>
                    </Grid>
                    {view === ViewType.cardView ? (
                        <OrganizationRolesListCards
                            orgId={orgId}
                            data={filteredRoles}
                            isLoading={isLoading}
                        />
                    ) : (
                        <OrganizationRolesListTable
                            orgId={orgId}
                            data={filteredRoles}
                            isLoading={isLoading}
                        />
                    )}
                </div>
            ) : (
                <></>
            )}

            {((id && detailData) || (id && id === "add")) && !isLoadingUsers && (
                <div style={{ zIndex: 1201 }}>
                    <OrganizationRolesDetail
                        orgId={orgId}
                        id={id}
                        isOrgAdministrator={isOrgAdmin}
                        isExternalUserRole={isExternalUser}
                        close={onCloseDetail}
                        refetch={refetch}
                        data={detailData}
                        isLoadingUsers={isLoadingUsers}
                    />
                </div>
            )}
        </>
    );
});

export default OrganizationRolesList;
